/**
 * OCT20A WS-ROLL2 (R3 / R4 / R5) — words and rows for the server's scheduling alerts. Pure; no I/O.
 *
 * The server is the only judge (schedule_alert_conflicts, written by evaluate_eod_overrun / the next-day recovery
 * detector / the 15-minute reminder engine — pg_cron, no browser). These helpers only word what it recorded:
 *   - an END-OF-DAY OVERRUN conflict: one per technician-day, every affected ticket listed with its own projected
 *     overtime ("Projected EOD Overrun — 42 Minutes"), including jobs a cascade pushed later;
 *   - a SCHEDULING DECISION conflict: work whose day ended unresolved (single-day or multiday), shown on today's board
 *     as "Scheduling Decision Required" — listed beside the lanes, never booked, so it holds no capacity.
 * Acknowledging is not resolving: the reminder line says so.
 */

export const SCHEDULING_DECISION_REQUIRED = 'Scheduling Decision Required';
export const EOD_OVERRUN_HEADING = 'Projected EOD Overrun';

/** "Projected EOD Overrun — 42 Minutes" (singular for one minute). */
export function formatEodOverrun(minutes: number | null | undefined): string {
  const n = Math.max(0, Math.round(Number(minutes) || 0));
  return `${EOD_OVERRUN_HEADING} — ${n} Minute${n === 1 ? '' : 's'}`;
}

export interface AlertConflictRow {
  id: string;
  kind: 'eod_overrun' | 'scheduling_decision' | string;
  technician_id: string | null;
  work_date: string;
  state: string;
  max_overrun_min: number | null;
  last_notified_at: string | null;
  next_reminder_at: string | null;
  notification_count: number | null;
  suppressed_since: string | null;
  acknowledged_at: string | null;
  summary: Record<string, unknown> | null;
}

export interface AlertTicketRow {
  conflict_id: string;
  ticket_id: string;
  active: boolean;
  overrun_min: number | null;
  projected_finish: string | null;
  pushed_by_cascade: boolean | null;
  conversion_id: string | null;
  /** OCT28 #9: the day boundary the overrun is measured against (for an overtime decision). */
  boundary_at?: string | null;
}

export interface AlertTicketInfo { id: string; simple_ticket_number: number | null; customer_name?: string | null }

export interface EodOverrunStripRow {
  conflictId: string;
  technicianName: string;
  workDate: string;
  maxOverrunMin: number;
  heading: string;
  tickets: Array<{ ticketId: string; label: string; overrunMin: number; pushedByCascade: boolean; awaitingDecision: boolean; text: string;
    /** OCT28 #9: what an overtime decision needs, and the approved plan's next day when the ticket is a chain head. */
    boundaryAt?: string | null; projectedFinish?: string | null; continuesOn?: string | null;
    /** OCT28 #9 (complete): which case this member is — it decides the words and which actions are real. */
    shape?: EodMemberShape;
    /** OCT28 #9 (complete): the customer's promise, judged separately from the technician's shift overrun. */
    dueOut?: DueOutJudgement | null }>;
  reminder: string;
}

export function alertTicketLabel(t: AlertTicketInfo | undefined, id: string): string {
  return t?.simple_ticket_number != null ? `#TKT-${String(t.simple_ticket_number).padStart(4, '0')}` : `#${id.slice(0, 8)}`;
}

/** The reminder line: when the next reminder goes out, held while the shop is closed, and that acknowledging is not resolving. */
export function describeReminderState(c: Pick<AlertConflictRow, 'next_reminder_at' | 'suppressed_since' | 'acknowledged_at' | 'notification_count'>,
  formatTime: (iso: string) => string): string {
  const parts: string[] = [];
  if (c.suppressed_since) parts.push('Reminders paused while the shop is closed; they resume when it opens');
  else if (c.next_reminder_at) parts.push(`Next reminder ${formatTime(c.next_reminder_at)} (every 15 minutes while the shop is open)`);
  if (c.acknowledged_at) parts.push('Acknowledged — still unresolved until a scheduling decision is carried out');
  return parts.join(' · ');
}

/** OCT28 #9: the words for an end-of-day overrun that has no decision record (nothing is approved or carried). */
export const EOD_NO_DECISION_RECORD_SHORT =
  'no end-of-day decision record exists for it yet; reassign, move or re-plan it from its ticket card';
export const EOD_NO_DECISION_RECORD_TEXT =
  'The overruns listed above have no decision record yet (for example, the head of an approved multi-day plan). '
  + 'Nothing has been extended, authorized or carried. Resolve each one from its ticket card — Reassign, Move Vehicle '
  + 'or OptiPlace — and the alert clears when the schedule fits.';

/**
 * OCT28 #9 (complete) — every member of an end-of-day overrun is one of these, and each has a real next step:
 *   awaiting_decision    a proposed end-of-day decision exists → a decider reviews it; anyone else sees who decides
 *   plan_head            heads an approved multi-day plan: the remainder already continues on a later day, yet today
 *                        still projects past the shift end → extend today (overtime) or re-plan today's part
 *   plan_continuation    is itself a later day of an approved plan → re-plan it, or authorize overtime
 *   started_no_record    started single-day work (a started repair is never offered a rollover proposal)
 *   unstarted_no_record  unstarted work with no decision record yet
 */
export type EodMemberShape = 'awaiting_decision' | 'plan_head' | 'plan_continuation' | 'started_no_record' | 'unstarted_no_record';

/** Facts about a member ticket the alert row does not carry (read from the ticket and its live continuation). */
export interface EodMemberFacts {
  started?: boolean | null;
  /** The ticket is itself a continuation day (rollover_of_ticket_id / rollover_conversion_id set). */
  isContinuation?: boolean | null;
  /** The customer's promised time (tickets.due_out; continuations carry the head's). */
  dueOut?: string | null;
  /** When the whole repair now finishes: the latest live continuation's finish, else this row's projected finish. */
  repairFinish?: string | null;
}

export function classifyEodMember(t: Pick<AlertTicketRow, 'conversion_id'>, continuesOn: string | null, f: EodMemberFacts = {}): EodMemberShape {
  if (t.conversion_id) return 'awaiting_decision';
  if (continuesOn) return 'plan_head';
  if (f.isContinuation) return 'plan_continuation';
  return f.started ? 'started_no_record' : 'unstarted_no_record';
}

export const EOD_STARTED_NO_RECORD_SHORT =
  'work already started, so no rollover is proposed for it; reassign the remainder or authorize overtime';
export const EOD_CONTINUATION_SHORT =
  'a later day of an approved multi-day plan; re-plan it from its ticket card (Reassign / OptiPlace) or authorize overtime';

/** "part of an approved multi-day plan (continues D): the remaining work already moves to D, but today still projects N min …" */
export function describePlanHead(continuesOn: string, overrunMin: number): string {
  return `part of an approved multi-day plan (continues ${continuesOn}): the remaining work already moves to ${continuesOn}, `
    + `but today still projects ${overrunMin} min past the shift end — extend today with overtime, or re-plan today's part from its ticket card`;
}

export interface DueOutJudgement { atRisk: boolean; text: string }

/**
 * The customer's promise is a different question from the technician's shift: overtime that still meets the due-out
 * is a staffing decision; a finish after the due-out breaks what the customer was told — even when today's part is
 * capped at the shift end and the miss lands on a later day of the plan.
 */
export function judgeDueOut(dueOut: string | null | undefined, repairFinish: string | null | undefined,
  formatWhen: (iso: string) => string): DueOutJudgement | null {
  const due = dueOut ? Date.parse(dueOut) : NaN;
  const fin = repairFinish ? Date.parse(repairFinish) : NaN;
  if (!Number.isFinite(due) || !Number.isFinite(fin)) return null;
  return fin > due
    ? { atRisk: true, text: `Customer due-out at risk — promised ${formatWhen(dueOut!)}, the repair now finishes ${formatWhen(repairFinish!)}` }
    : { atRisk: false, text: `Customer due-out still met (promised ${formatWhen(dueOut!)}) — this is a technician overrun only` };
}

/** Open end-of-day overrun conflicts for `today` and later, per technician-day, tickets ordered by overrun. */
export function buildEodOverrunRows(
  conflicts: AlertConflictRow[],
  tickets: AlertTicketRow[],
  info: AlertTicketInfo[],
  techNames: Record<string, string>,
  today: string,
  formatTime: (iso: string) => string,
  /** OCT28 #9: ticket id → scheduled date of its live continuation (approved multi-day plan). */
  continuations: Record<string, string> = {},
  /** OCT28 #9 (complete): per-ticket facts (started, continuation, due-out, whole-repair finish). */
  facts: Record<string, EodMemberFacts> = {},
  formatWhen: (iso: string) => string = formatTime,
): EodOverrunStripRow[] {
  const byTicket = new Map(info.map((t) => [t.id, t]));
  return conflicts
    .filter((c) => c.kind === 'eod_overrun' && c.state === 'open' && c.work_date >= today)
    .sort((a, b) => a.work_date.localeCompare(b.work_date) || (b.max_overrun_min ?? 0) - (a.max_overrun_min ?? 0))
    .map((c) => {
      const rows = tickets.filter((t) => t.conflict_id === c.id && t.active)
        .sort((a, b) => (b.overrun_min ?? 0) - (a.overrun_min ?? 0) || a.ticket_id.localeCompare(b.ticket_id))
        .map((t) => {
          const over = Math.max(0, t.overrun_min ?? 0);
          const label = alertTicketLabel(byTicket.get(t.ticket_id), t.ticket_id);
          const awaiting = !!t.conversion_id;
          const continuesOn = continuations[t.ticket_id] ?? null;
          const f = facts[t.ticket_id] ?? {};
          const shape = classifyEodMember(t, continuesOn, f);
          // OCT28 #9: every member says which case it is and what can be done (none reads as a waiting decision).
          const why = shape === 'awaiting_decision' ? 'awaiting approval of its end-of-day decision'
            : shape === 'plan_head' ? describePlanHead(continuesOn!, over)
              : shape === 'plan_continuation' ? EOD_CONTINUATION_SHORT
                : shape === 'started_no_record' ? EOD_STARTED_NO_RECORD_SHORT : EOD_NO_DECISION_RECORD_SHORT;
          const text = over > 0
            ? `${formatEodOverrun(over)}${t.pushed_by_cascade ? ' (pushed later by the job before it)' : ''} — ${why}`
            : awaiting ? 'End-of-day decision awaiting approval' : EOD_NO_DECISION_RECORD_SHORT;
          const dueOut = judgeDueOut(f.dueOut, f.repairFinish ?? t.projected_finish, formatWhen);
          return { ticketId: t.ticket_id, label, overrunMin: over, pushedByCascade: !!t.pushed_by_cascade, awaitingDecision: awaiting, text,
            boundaryAt: t.boundary_at ?? null, projectedFinish: t.projected_finish ?? null, continuesOn, shape, dueOut };
        });
      return {
        conflictId: c.id,
        technicianName: (c.technician_id && techNames[c.technician_id]) || 'Technician',
        workDate: c.work_date,
        maxOverrunMin: Math.max(0, c.max_overrun_min ?? 0),
        heading: formatEodOverrun(c.max_overrun_min),
        tickets: rows,
        reminder: describeReminderState(c, formatTime),
      };
    })
    .filter((r) => r.tickets.length > 0);
}

/** Next-day decisions that the alert engine still holds open (incl. a recorded decision not yet carried out). */
export interface SchedulingDecisionAlertRow {
  conflictId: string;
  ticketId: string;
  workDate: string;
  source: 'stale_chain_tail' | 'stale_single_day' | 'other';
  recordedDecision: string | null;
  reminder: string;
}

export function buildSchedulingDecisionAlertRows(conflicts: AlertConflictRow[], today: string,
  formatTime: (iso: string) => string): SchedulingDecisionAlertRow[] {
  return conflicts
    .filter((c) => c.kind === 'scheduling_decision' && c.state === 'open' && c.work_date < today)
    .map((c) => {
      const s = (c.summary ?? {}) as Record<string, unknown>;
      const src = s.source === 'stale_chain_tail' || s.source === 'stale_single_day' ? s.source : 'other';
      return {
        conflictId: c.id,
        ticketId: String(s.ticket_id ?? ''),
        workDate: c.work_date,
        source: src as SchedulingDecisionAlertRow['source'],
        recordedDecision: typeof s.recorded_decision === 'string' ? s.recorded_decision : null,
        reminder: describeReminderState(c, formatTime),
      };
    })
    .filter((r) => r.ticketId)
    .sort((a, b) => a.workDate.localeCompare(b.workDate) || a.ticketId.localeCompare(b.ticketId));
}

const DECISION_WORDS: Record<string, string> = {
  reschedule: 'Reschedule', reassign: 'Reassign', authorize_overtime: 'Authorize overtime', approve_rollover: 'Approve rollover',
};

/** "Decision recorded (Reschedule) — the work still sits on 2026-10-19; move it from the card to resolve this." */
export function describeRecordedDecision(decision: string | null, workDate: string): string | null {
  if (!decision) return null;
  return `Decision recorded (${DECISION_WORDS[decision] ?? decision}) — the work still sits on ${workDate}; carry it out from the card to resolve this`;
}
